// A tooltip that opens on hover and on keyboard focus, closes on Esc (without closing the
// overlay underneath) and is announced through aria-describedby. Its body renders only while open.
import type { ComponentChildren } from 'preact';
import { useId, useState } from 'preact/hooks';
import './tooltip.css';

interface Props {
  /** The visible trigger content (a number, a label). */
  children: ComponentChildren;
  /** Rendered only while open, so expensive factor lists are computed lazily. */
  content: () => ComponentChildren;
  /** Accessible name for the trigger. */
  label?: string;
  class?: string;
  /** Place the bubble above instead of below (for rows near the bottom of a panel). */
  above?: boolean;
}

export function Tooltip({ children, content, label, class: cls, above }: Props) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <span
      class={`tip${above === true ? ' tip--above' : ''}${cls === undefined ? '' : ` ${cls}`}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span
        class="tip__trigger"
        tabIndex={0}
        role="button"
        aria-label={label}
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && open) {
            e.stopPropagation();
            setOpen(false);
          } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            setOpen(!open);
          }
        }}
      >
        {children}
      </span>
      {open && (
        <span class="tip__bubble" role="tooltip" id={id}>
          {content()}
        </span>
      )}
    </span>
  );
}
